// Advanced connectivity testing features
// Additional comprehensive network diagnostics

// CDN/Cache Detection
export interface CDNInfo {
  isCDN: boolean;
  provider?: string;
  cacheStatus?: 'HIT' | 'MISS' | 'BYPASS' | 'EXPIRED' | 'DYNAMIC';
  popLocation?: string;
  age?: number;
  rayId?: string;
  server?: string;
  via?: string;
}

export async function detectCDN(url: string): Promise<CDNInfo> {
  try {
    const response = await fetch(url, {
      method: 'HEAD',
      cache: 'no-store',
      signal: AbortSignal.timeout(10000) // 10s timeout
    });
    const headers = response.headers;

    const result: CDNInfo = {
      isCDN: false,
      server: headers.get('server') || undefined,
      via: headers.get('via') || undefined
    };

    // Cloudflare detection
    if (headers.has('cf-ray') || headers.get('server')?.includes('cloudflare')) {
      result.isCDN = true;
      result.provider = 'Cloudflare';
      result.rayId = headers.get('cf-ray') || undefined;
      result.popLocation = headers.get('cf-ray')?.split('-')[1] || undefined;

      const cfCacheStatus = headers.get('cf-cache-status');
      if (cfCacheStatus) {
        result.cacheStatus = cfCacheStatus as any;
      }
    }

    // Fastly detection
    else if (headers.has('fastly-debug-digest') || headers.get('x-served-by')?.includes('cache')) {
      result.isCDN = true;
      result.provider = 'Fastly';
      const xCache = headers.get('x-cache');
      if (xCache) {
        result.cacheStatus = xCache.includes('HIT') ? 'HIT' : 'MISS';
      }
    }

    // Akamai detection
    else if (headers.has('x-akamai-request-id') || headers.get('server')?.includes('AkamaiGHost')) {
      result.isCDN = true;
      result.provider = 'Akamai';
    }

    // CloudFront detection
    else if (headers.has('x-amz-cf-id') || headers.has('x-amz-cf-pop')) {
      result.isCDN = true;
      result.provider = 'CloudFront';
      result.popLocation = headers.get('x-amz-cf-pop') || undefined;
      const xCache = headers.get('x-cache');
      if (xCache) {
        result.cacheStatus = xCache.includes('Hit') ? 'HIT' : 'MISS';
      }
    }

    // Generic CDN detection
    else if (headers.has('x-cache') || headers.has('via')) {
      result.isCDN = true;
      result.provider = 'Unknown CDN';
      const xCache = headers.get('x-cache');
      if (xCache) {
        result.cacheStatus = xCache.toUpperCase().includes('HIT') ? 'HIT' : 'MISS';
      }
    }

    // Parse Age header
    const age = headers.get('age');
    if (age) {
      result.age = parseInt(age, 10);
    }

    return result;
  } catch (error) {
    return { isCDN: false };
  }
}

// TLS/SSL Deep Analysis
export interface TLSInfo {
  protocol?: string; // TLS 1.2, TLS 1.3
  secureConnection: boolean;
  cipher?: string;
  certificateValid?: boolean;
  certificateIssuer?: string;
  certificateExpiry?: Date;
  daysUntilExpiry?: number;
  hsts?: boolean;
  hstsMaxAge?: number;
  securityGrade?: 'A+' | 'A' | 'B' | 'C' | 'F';
  warnings: string[];
}

export async function analyzeTLS(url: string): Promise<TLSInfo> {
  const warnings: string[] = [];

  try {
    const parsedUrl = new URL(url);

    // Try with CORS first (to get headers), fall back to no-cors
    let response;
    let headers: Headers;

    try {
      response = await fetch(url, {
        method: 'HEAD',
        signal: AbortSignal.timeout(10000) // 10s timeout
      });
      headers = response.headers;
    } catch (corsError) {
      // CORS failed, try no-cors mode (won't get headers but can check protocol)
      response = await fetch(url, {
        method: 'HEAD',
        mode: 'no-cors',
        signal: AbortSignal.timeout(10000)
      });
      // no-cors mode returns opaque response with no headers
      headers = new Headers(); // Empty headers
      warnings.push('CORS restriction - limited security analysis available');
    }

    // Get performance entry for TLS timing
    const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const entry = entries[entries.length - 1];

    const result: TLSInfo = {
      secureConnection: parsedUrl.protocol === 'https:',
      warnings
    };

    if (!result.secureConnection) {
      warnings.push('Insecure HTTP connection - no encryption');
      result.securityGrade = 'F';
      return result;
    }

    // Detect TLS version from protocol (limited in browser)
    if (entry && entry.nextHopProtocol) {
      // HTTP/2 and HTTP/3 require TLS 1.2+
      if (entry.nextHopProtocol === 'h2' || entry.nextHopProtocol === 'h3') {
        result.protocol = 'TLS 1.2+';
      } else {
        result.protocol = 'TLS';
      }
    }

    // Check HSTS
    const hsts = headers.get('strict-transport-security');
    if (hsts) {
      result.hsts = true;
      const maxAgeMatch = hsts.match(/max-age=(\d+)/);
      if (maxAgeMatch) {
        result.hstsMaxAge = parseInt(maxAgeMatch[1], 10);
      }
    } else {
      warnings.push('HSTS not enabled - missing Strict-Transport-Security header');
    }

    // Check for certificate info via API (using badssl.com-like service)
    // Note: Browsers don't expose certificate details directly for security
    // We can only make educated guesses based on other signals

    // Security grade calculation
    let grade = 'A';

    if (!result.hsts) {
      grade = 'B';
    }

    if (result.protocol === 'TLS 1.2+') {
      // Modern TLS
    } else {
      warnings.push('TLS version could not be confirmed as 1.3');
      grade = 'B';
    }

    // Check security headers
    const csp = headers.get('content-security-policy');
    const xfo = headers.get('x-frame-options');
    const xcto = headers.get('x-content-type-options');

    if (!csp) {
      warnings.push('Missing Content-Security-Policy header');
    }
    if (!xfo) {
      warnings.push('Missing X-Frame-Options header');
    }
    if (!xcto) {
      warnings.push('Missing X-Content-Type-Options header');
    }

    if (result.hsts && csp && xfo && xcto) {
      result.securityGrade = 'A+';
    } else if (result.hsts) {
      result.securityGrade = 'A';
    } else {
      result.securityGrade = grade as any;
    }

    result.warnings = warnings;
    return result;
  } catch (error: any) {
    return {
      secureConnection: false,
      warnings: [`Failed to analyze TLS configuration: ${error.message || 'Unknown error'}`],
      securityGrade: 'F'
    };
  }
}

// IPv4 vs IPv6 Comparison
export interface IPVersionTest {
  ipv4Available: boolean;
  ipv6Available: boolean;
  ipv4Latency?: number;
  ipv6Latency?: number;
  preferredVersion?: 'IPv4' | 'IPv6';
  dualStack: boolean;
}

export async function testIPVersions(hostname: string): Promise<IPVersionTest> {
  const url = new URL(hostname.startsWith('http') ? hostname : `https://${hostname}`);
  const host = url.hostname;

  const result: IPVersionTest = {
    ipv4Available: false,
    ipv6Available: false,
    dualStack: false
  };

  try {
    // Test IPv4 via DNS
    const ipv4Test = await testDNSForType(host, 'A');
    if (ipv4Test.ip) {
      result.ipv4Available = true;
      result.ipv4Latency = ipv4Test.latency;
    }

    // Test IPv6 via DNS
    const ipv6Test = await testDNSForType(host, 'AAAA');
    if (ipv6Test.ip) {
      result.ipv6Available = true;
      result.ipv6Latency = ipv6Test.latency;
    }

    result.dualStack = result.ipv4Available && result.ipv6Available;

    // Determine preferred version based on latency
    if (result.ipv4Latency && result.ipv6Latency) {
      result.preferredVersion = result.ipv6Latency < result.ipv4Latency ? 'IPv6' : 'IPv4';
    } else if (result.ipv4Available) {
      result.preferredVersion = 'IPv4';
    } else if (result.ipv6Available) {
      result.preferredVersion = 'IPv6';
    }

    return result;
  } catch (error) {
    return result;
  }
}

async function testDNSForType(hostname: string, type: 'A' | 'AAAA'): Promise<{ ip?: string; latency: number }> {
  const start = performance.now();

  try {
    // Use Google DNS JSON API (supports both A and AAAA)
    const response = await fetch(`https://dns.google/resolve?name=${hostname}&type=${type}`, {
      signal: AbortSignal.timeout(5000) // 5s timeout
    });
    const data = await response.json();
    const latency = performance.now() - start;

    return {
      ip: data.Answer?.[0]?.data,
      latency
    };
  } catch (error) {
    return { latency: performance.now() - start };
  }
}

// MTU/Fragmentation Detection
export interface MTUInfo {
  estimatedMTU: number;
  fragmentationDetected: boolean;
  commonMTUs: { size: number; name: string; success: boolean }[];
}

export async function detectMTU(url: string): Promise<MTUInfo> {
  // Common MTU sizes
  const mtuSizes = [
    { size: 1500, name: 'Ethernet (standard)' },
    { size: 1492, name: 'PPPoE' },
    { size: 1280, name: 'IPv6 minimum' },
    { size: 1450, name: 'VPN typical' },
    { size: 9000, name: 'Jumbo frames' }
  ];

  const results = [];
  let estimatedMTU = 1500;

  for (const mtu of mtuSizes) {
    try {
      // Test by fetching with timeout
      // This is indirect - browsers don't give us direct MTU control
      const start = performance.now();
      const response = await fetch(url, {
        method: 'HEAD', // Use HEAD instead of GET to avoid downloading
        cache: 'no-store',
        signal: AbortSignal.timeout(5000) // 5s timeout
      });
      const latency = performance.now() - start;

      results.push({
        ...mtu,
        success: response.ok,
        latency
      });

      if (response.ok && mtu.size > estimatedMTU) {
        estimatedMTU = mtu.size;
      }
    } catch (error) {
      results.push({
        ...mtu,
        success: false
      });
    }
  }

  return {
    estimatedMTU,
    fragmentationDetected: estimatedMTU < 1500,
    commonMTUs: results
  };
}

// Network Quality Metrics (MOS Score)
export interface NetworkQuality {
  mosScore: number; // 1-5 scale
  rating: 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'Bad';
  voipQuality: string;
  videoQuality: string;
  metrics: {
    latency: number;
    jitter: number;
    packetLoss: number;
  };
}

export function calculateMOS(latency: number, jitter: number, packetLoss: number): NetworkQuality {
  // ITU-T G.107 E-Model simplified calculation
  // R-factor calculation
  let R = 93.2; // Base R-factor

  // Latency impact (delay)
  const effectiveLatency = latency + (jitter * 2); // Effective latency
  if (effectiveLatency < 160) {
    R -= effectiveLatency / 40;
  } else {
    R -= (effectiveLatency - 120) / 10;
  }

  // Packet loss impact
  R -= packetLoss * 2.5;

  // Clamp R-factor
  R = Math.max(0, Math.min(100, R));

  // Convert R-factor to MOS
  let mos: number;
  if (R < 0) {
    mos = 1;
  } else if (R > 100) {
    mos = 4.5;
  } else {
    mos = 1 + (0.035 * R) + (R * (R - 60) * (100 - R) * 0.000007);
  }

  // Clamp MOS to 1-5
  mos = Math.max(1, Math.min(5, mos));

  // Determine rating
  let rating: NetworkQuality['rating'];
  let voipQuality: string;
  let videoQuality: string;

  if (mos >= 4.3) {
    rating = 'Excellent';
    voipQuality = 'HD Voice Quality';
    videoQuality = '4K Video Capable';
  } else if (mos >= 4.0) {
    rating = 'Good';
    voipQuality = 'Good Voice Quality';
    videoQuality = '1080p Video Capable';
  } else if (mos >= 3.6) {
    rating = 'Fair';
    voipQuality = 'Acceptable Voice Quality';
    videoQuality = '720p Video Capable';
  } else if (mos >= 3.0) {
    rating = 'Poor';
    voipQuality = 'Degraded Voice Quality';
    videoQuality = '480p Video Only';
  } else {
    rating = 'Bad';
    voipQuality = 'Unusable for Voice';
    videoQuality = 'Unusable for Video';
  }

  return {
    mosScore: parseFloat(mos.toFixed(2)),
    rating,
    voipQuality,
    videoQuality,
    metrics: {
      latency,
      jitter,
      packetLoss
    }
  };
}

// Connection Stability Test
export interface StabilityTest {
  duration: number; // seconds
  samples: { timestamp: number; latency: number }[];
  avgLatency: number;
  minLatency: number;
  maxLatency: number;
  stdDev: number;
  drops: number;
  stability: 'Stable' | 'Moderate' | 'Unstable';
}

export async function testConnectionStability(
  url: string,
  durationSeconds: number = 60,
  onProgress?: (sample: { timestamp: number; latency: number }) => void,
  signal?: AbortSignal
): Promise<StabilityTest> {
  const samples: { timestamp: number; latency: number }[] = [];
  const startTime = Date.now();
  const endTime = startTime + (durationSeconds * 1000);
  let drops = 0;

  while (Date.now() < endTime) {
    // Check if test was cancelled
    if (signal?.aborted) {
      throw new Error('Connection stability test cancelled');
    }

    const start = performance.now();

    try {
      await fetch(url, {
        method: 'HEAD',
        cache: 'no-store',
        signal: AbortSignal.timeout(10000) // 10s timeout per request
      });
      const latency = performance.now() - start;

      const sample = { timestamp: Date.now() - startTime, latency };
      samples.push(sample);

      if (onProgress) {
        onProgress(sample);
      }
    } catch (error: any) {
      if (error.name === 'AbortError' && signal?.aborted) {
        throw new Error('Connection stability test cancelled');
      }
      // Otherwise it's a timeout or network error
      drops++;
    }

    // Wait 1 second between samples
    await new Promise(resolve => setTimeout(resolve, 1000));
  }

  // Calculate statistics
  const latencies = samples.map(s => s.latency);
  const avgLatency = latencies.reduce((a, b) => a + b, 0) / latencies.length;
  const minLatency = Math.min(...latencies);
  const maxLatency = Math.max(...latencies);

  // Standard deviation
  const variance = latencies.reduce((sum, lat) => sum + Math.pow(lat - avgLatency, 2), 0) / latencies.length;
  const stdDev = Math.sqrt(variance);

  // Determine stability
  let stability: StabilityTest['stability'];
  const coefficientOfVariation = stdDev / avgLatency;

  if (coefficientOfVariation < 0.1 && drops === 0) {
    stability = 'Stable';
  } else if (coefficientOfVariation < 0.3 && drops < 3) {
    stability = 'Moderate';
  } else {
    stability = 'Unstable';
  }

  return {
    duration: durationSeconds,
    samples,
    avgLatency,
    minLatency,
    maxLatency,
    stdDev,
    drops,
    stability
  };
}

// Port Scanning (Limited)
export interface PortScanResult {
  port: number;
  service: string;
  open: boolean;
  latency?: number;
  cors?: boolean;
}

export async function scanPorts(hostname: string): Promise<PortScanResult[]> {
  const url = new URL(hostname.startsWith('http') ? hostname : `https://${hostname}`);
  const host = url.hostname;

  const commonPorts = [
    { port: 80, service: 'HTTP' },
    { port: 443, service: 'HTTPS' },
    { port: 8080, service: 'HTTP Alt' },
    { port: 8443, service: 'HTTPS Alt' },
    { port: 3000, service: 'Dev Server' },
    { port: 5000, service: 'Flask/Dev' },
    { port: 3306, service: 'MySQL' },
    { port: 5432, service: 'PostgreSQL' },
    { port: 27017, service: 'MongoDB' },
    { port: 6379, service: 'Redis' }
  ];

  const results: PortScanResult[] = [];

  for (const { port, service } of commonPorts) {
    const start = performance.now();
    const testUrl = `${url.protocol}//${host}:${port}`;

    try {
      const response = await fetch(testUrl, {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-store',
        signal: AbortSignal.timeout(3000)
      });

      const latency = performance.now() - start;

      results.push({
        port,
        service,
        open: true,
        latency,
        cors: response.type !== 'opaque'
      });
    } catch (error: any) {
      // Port might be open but blocking browser requests
      const latency = performance.now() - start;

      results.push({
        port,
        service,
        open: latency < 2900, // If timeout was quick, port likely filtered
        latency: latency < 2900 ? latency : undefined
      });
    }
  }

  return results;
}

// Proxy/VPN Detection
export interface ProxyInfo {
  likelyProxy: boolean;
  likelyVPN: boolean;
  indicators: string[];
  ipMismatch: boolean;
  dnsLeak: boolean;
  webRTCLeak: boolean;
}

export async function detectProxy(localIPs: string[], publicIP?: string): Promise<ProxyInfo> {
  const indicators: string[] = [];
  let likelyProxy = false;
  let likelyVPN = false;

  // Check for private IP exposure (WebRTC leak)
  const webRTCLeak = localIPs.some(ip =>
    ip.startsWith('10.') ||
    ip.startsWith('172.') ||
    ip.startsWith('192.168.') ||
    ip.startsWith('fc00:') ||
    ip.startsWith('fd00:')
  );

  if (webRTCLeak && publicIP) {
    indicators.push('WebRTC leak detected - local IP exposed');
  }

  // Check for IP mismatch (crude detection)
  const ipMismatch = !!(localIPs.length > 0 && publicIP && !localIPs.includes(publicIP));

  if (ipMismatch) {
    indicators.push('IP mismatch between local and public - NAT or proxy detected');
  }

  // Check DNS servers (would need additional API)
  // Placeholder - real implementation would need DNS leak test API
  const dnsLeak = false;

  // Heuristics for VPN detection
  if (publicIP) {
    // Common VPN IP ranges (this is a simplified check)
    // Real implementation would use IP intelligence API
    likelyVPN = false; // Placeholder
  }

  // Heuristics for proxy detection
  if (ipMismatch && localIPs.length > 1) {
    likelyProxy = true;
    indicators.push('Multiple local IPs with different public IP suggests proxy/NAT');
  }

  return {
    likelyProxy,
    likelyVPN,
    indicators,
    ipMismatch,
    dnsLeak,
    webRTCLeak
  };
}

// QoS Detection
export interface QoSInfo {
  throttlingDetected: boolean;
  shapingDetected: boolean;
  avgThroughput: number;
  variance: number;
  suspectedLimit?: number;
}

export async function detectQoS(url: string): Promise<QoSInfo> {
  const samples: number[] = [];
  const sampleCount = 5;

  // Test multiple times to detect throttling patterns
  for (let i = 0; i < sampleCount; i++) {
    const start = performance.now();

    try {
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        signal: AbortSignal.timeout(15000) // 15s timeout per download
      });

      const blob = await response.blob();
      const duration = (performance.now() - start) / 1000; // seconds
      const throughput = (blob.size * 8) / duration / 1000000; // Mbps

      samples.push(throughput);
    } catch (error: any) {
      // Skip failed samples (timeout or network error)
      if (error.name !== 'TimeoutError' && error.name !== 'AbortError') {
        // Log unexpected errors but continue
        console.warn('QoS test sample failed:', error.message);
      }
    }

    // Wait between samples
    await new Promise(resolve => setTimeout(resolve, 500));
  }

  if (samples.length === 0) {
    return {
      throttlingDetected: false,
      shapingDetected: false,
      avgThroughput: 0,
      variance: 0
    };
  }

  const avgThroughput = samples.reduce((a, b) => a + b, 0) / samples.length;
  const variance = samples.reduce((sum, val) => sum + Math.pow(val - avgThroughput, 2), 0) / samples.length;

  // Detect throttling if throughput decreases over time
  const throttlingDetected = samples[samples.length - 1] < samples[0] * 0.7;

  // Detect shaping if variance is very low (unusually consistent)
  const shapingDetected = variance < 0.1;

  return {
    throttlingDetected,
    shapingDetected,
    avgThroughput,
    variance,
    suspectedLimit: throttlingDetected ? samples[samples.length - 1] : undefined
  };
}

// Comparative Analysis
export interface ComparativeResult {
  target: string;
  baseline: string;
  targetLatency: number;
  baselineLatency: number;
  relativeDifference: number; // percentage
  verdict: 'Much Faster' | 'Faster' | 'Similar' | 'Slower' | 'Much Slower';
}

export async function compareEndpoints(target: string, baseline: string = 'https://www.google.com'): Promise<ComparativeResult> {
  // Test target
  const targetSamples = [];
  for (let i = 0; i < 3; i++) {
    const start = performance.now();
    try {
      await fetch(target, {
        method: 'HEAD',
        cache: 'no-store',
        signal: AbortSignal.timeout(10000) // 10s timeout
      });
      targetSamples.push(performance.now() - start);
    } catch (e) {
      targetSamples.push(9999);
    }
  }

  // Test baseline
  const baselineSamples = [];
  for (let i = 0; i < 3; i++) {
    const start = performance.now();
    try {
      await fetch(baseline, {
        method: 'HEAD',
        cache: 'no-store',
        signal: AbortSignal.timeout(10000) // 10s timeout
      });
      baselineSamples.push(performance.now() - start);
    } catch (e) {
      baselineSamples.push(9999);
    }
  }

  const targetLatency = targetSamples.reduce((a, b) => a + b, 0) / targetSamples.length;
  const baselineLatency = baselineSamples.reduce((a, b) => a + b, 0) / baselineSamples.length;

  const relativeDifference = ((targetLatency - baselineLatency) / baselineLatency) * 100;

  let verdict: ComparativeResult['verdict'];
  if (relativeDifference < -50) {
    verdict = 'Much Faster';
  } else if (relativeDifference < -20) {
    verdict = 'Faster';
  } else if (relativeDifference > 50) {
    verdict = 'Much Slower';
  } else if (relativeDifference > 20) {
    verdict = 'Slower';
  } else {
    verdict = 'Similar';
  }

  return {
    target,
    baseline,
    targetLatency,
    baselineLatency,
    relativeDifference,
    verdict
  };
}
